import { baseURL } from '../../../api/baseUrl';
import {
  CMSApiResponse,
  CMSSearchResponse,
  CMSSearchResult,
  CMSViewMode
} from '../types/CMSTypes';

// Helper function for safe URL encoding that handles forward slashes
const safeUrlEncode = (value: string): string => {
  return encodeURIComponent(value).replace(/%2F/g, '%252F');
};

// ===== CORE CMS API FUNCTIONS =====

// Search for categories by keyword
export const searchCategoryNames = async (
  keyword: string,
  filters?: {
    roles?: string[];
    item_groups?: string[];
    applications?: string[];
    category_type?: string[];
  } | null
): Promise<CMSSearchResponse> => {
  try {
    const safeKeyword = safeUrlEncode(keyword);

    // Build URL with optional filters
    let url = `/cms-category/search-names/${safeKeyword}`;

    // Always add consumer role filter
    const defaultFilters: any = {
      roles: ['consumer']
    };

    if (filters) {
      // Merge with default filters
      if (filters.item_groups?.length) defaultFilters.item_groups = filters.item_groups;
      if (filters.applications?.length) defaultFilters.applications = filters.applications;
      if (filters.category_type?.length) defaultFilters.category_type = filters.category_type;
    }

    // Add filters to URL
    const encodedFilters = encodeURIComponent(JSON.stringify(defaultFilters));
    url += `?filters=${encodedFilters}`;

    const response = await baseURL.get(url, {
      headers: {
        'Content-Type': 'application/json',
      }
    });

    return response.data;
  } catch (error) {
    console.error('Error searching category names:', error);
    throw error;
  }
};

// Get CMS page data for a specific category
export const getCMSPage = async (
  categoryId: string,
  onlyPublished: boolean = true
): Promise<CMSApiResponse> => {
  try {
    const response = await baseURL.get(
      `/cms-category/${categoryId}/cms-page/${onlyPublished}`,
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    return response.data;
  } catch (error) {
    console.error('Error fetching CMS page:', error);
    throw error;
  }
};

// ===== CATEGORY OVERRIDE API =====

// Get CMS page data for a category override
export const getCategoryOverrideData = async (
  overrideId: string,
  onlyPublished: boolean = true
): Promise<CMSApiResponse> => {
  try {
    const response = await baseURL.get(
      `/cms-category-override/${overrideId}/cms-page/${onlyPublished}`,
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    return response.data;
  } catch (error) {
    console.error('Error fetching category override data:', error);
    throw error;
  }
};

// Helper function to get consumer override ID from search response
const getConsumerOverrideId = (categoryData: any): string | null => {
  const overrides = categoryData.override_categories || [];

  const consumerOverride = overrides.find((override: any) =>
    override.keycloak_role_key === 'consumer' &&
    override.is_published
  );

  return consumerOverride?.id || null;
};

// ===== HOME PAGE SPECIFIC API =====

// Get Home page data
export const getHomePageData = async (viewMode: CMSViewMode = 'live'): Promise<CMSApiResponse> => {
  try {
    const searchResponse = await searchCategoryNames('home');

    if (!searchResponse.success || !searchResponse.results?.home) {
      throw new Error('Home category not found');
    }

    const homeCategory = searchResponse.results.home;
    const consumerOverrideId = getConsumerOverrideId(homeCategory);
    const onlyPublished = true;

    let cmsResponse: CMSApiResponse;

    if (consumerOverrideId) {
      console.log('Loading consumer override for home page:', consumerOverrideId);
      cmsResponse = await getCategoryOverrideData(consumerOverrideId, onlyPublished);
    } else {
      console.log('Loading standard home page (no consumer override)');
      cmsResponse = await getCMSPage(homeCategory.id, onlyPublished);
    }

    if (!cmsResponse.success) {
      throw new Error('Failed to load home page CMS data');
    }

    return cmsResponse;

  } catch (error) {
    console.error('Error fetching home page data:', error);
    throw error;
  }
};

// ===== ITEM GROUP PAGE SPECIFIC API =====

// Get CMS data for categories by category name with preferred category type
export const getCategoryPageData = async (
  categoryName: string,
  viewMode: CMSViewMode = 'live',
  preferredCategoryType?: string
): Promise<CMSApiResponse> => {
  try {
    const searchResponse = await searchCategoryNames(categoryName);

    if (!searchResponse.success || !searchResponse.results) {
      throw new Error(`Category '${categoryName}' not found`);
    }

    const allMatches = [
      ...(searchResponse.results.home ? [searchResponse.results.home] : []),
      ...(searchResponse.results.group || []),
      ...(searchResponse.results.item_group || []),
      ...(searchResponse.results.brand || []),
      ...(searchResponse.results.application || []),
      ...(searchResponse.results.application_type || []),
      ...(searchResponse.results.collection || []),
      ...(searchResponse.results.non_default || [])
    ].filter(category =>
      category.category_name.toLowerCase() === categoryName.toLowerCase()
    );

    if (allMatches.length === 0) {
      throw new Error(`Category '${categoryName}' not found`);
    }

    let categoryMatch = null;

    if (preferredCategoryType) {
      categoryMatch = allMatches.find(category =>
        category.category_type === preferredCategoryType
      );

      if (categoryMatch) {
        console.log(`Selected preferred category type: ${preferredCategoryType} for ${categoryName}`);
      }
    }

    if (!categoryMatch) {
      const categoryTypePriority = [
        'item_group',
        'group',
        'collection',
        'application',
        'brand',
        'home',
        'about_us',
        'careers',
        'stores',
        'contact_us',
        'new_arrival'
      ];

      for (const priorityType of categoryTypePriority) {
        categoryMatch = allMatches.find(category => category.category_type === priorityType);
        if (categoryMatch) {
          console.log(`Selected category type: ${priorityType} for ${categoryName}`);
          break;
        }
      }
    }

    if (!categoryMatch) {
      categoryMatch = allMatches[0];
      console.warn(`No priority match found, using first match: ${categoryMatch.category_type}`);
    }

    const consumerOverrideId = getConsumerOverrideId(categoryMatch);
    const onlyPublished = true;

    let cmsResponse: CMSApiResponse;

    if (consumerOverrideId) {
      console.log(`Loading consumer override for category '${categoryName}':`, consumerOverrideId);
      cmsResponse = await getCategoryOverrideData(consumerOverrideId, onlyPublished);
    } else {
      cmsResponse = await getCMSPage(categoryMatch.id, onlyPublished);
    }

    if (!cmsResponse.success) {
      throw new Error(`Failed to load CMS data for category '${categoryName}'`);
    }

    return cmsResponse;

  } catch (error) {
    console.error(`Error fetching category '${categoryName}' page data:`, error);
    throw error;
  }
};

// Backward compatibility - map item_group codes to category names
const ITEM_GROUP_TO_CATEGORY_MAP: Record<string, string> = {
  '000': 'homeware',
  '001': 'wallpaper',
  '004': 'rugs',
  '005': 'fabric',
  '006': 'bedroom',
  '007': 'illumination',
  '008': 'tableware',
  '009': 'furniture',
  '010': 'decorative',
  '013': 'Throws, Quilts and Furs',
  '014': 'wall decor',
  '015': 'curtains',
  '016': 'bathroom',
  '018': 'scatters'
};

// Get CMS data for item group categories (backward compatibility)
export const getItemGroupPageData = async (itemGroup: string, viewMode: CMSViewMode = 'live'): Promise<CMSApiResponse> => {
  const categoryName = ITEM_GROUP_TO_CATEGORY_MAP[itemGroup];

  if (!categoryName) {
    throw new Error(`Item group '${itemGroup}' not mapped to category name`);
  }

  return getCategoryPageData(categoryName, viewMode);
};

// ===== CUSTOM CMS PAGE API =====

/**
 * Search without the consumer role filter — needed for custom pages
 * which may not be tagged with a specific role.
 */
const searchCategoryNamesRaw = async (keyword: string): Promise<CMSSearchResponse> => {
  try {
    const safeKeyword = encodeURIComponent(keyword).replace(/%2F/g, '%252F');
    const url = `/cms-category/search-names/${safeKeyword}`;

    const response = await baseURL.get(url, {
      headers: { 'Content-Type': 'application/json' }
    });

    return response.data;
  } catch (error) {
    console.error('Error searching category names (raw):', error);
    throw error;
  }
};

/**
 * Converts a URL slug back to a search-friendly name.
 * e.g. "test-high-performance" → "Test High Performance"
 */
const slugToName = (slug: string): string => {
  return slug
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

/**
 * Slugify a name the same way the hub derives public URLs, so a slug
 * round-trips to the category's display_name (or category_name).
 * e.g. "Outdoor" → "outdoor", "Fabric Performance" → "fabric-performance"
 *
 * Punctuation is stripped after spaces become dashes, so runs of dashes are
 * collapsed and the ends trimmed: "Stools, ottomans & benches" →
 * "stools-ottomans-benches" (not "stools-ottomans--benches").
 */
export const slugify = (value: string): string =>
  value
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-{2,}/g, '-')
    .replace(/^-+|-+$/g, '');

/**
 * Custom categories matching a search keyword, or null if the search failed.
 *
 * The backend buckets results by category_sub_type, so a custom page built from a
 * template (e.g. sub_type 'application') lands in that type's bucket rather than
 * non_default/custom. Search every bucket for custom pages.
 */
const searchCustomCandidates = async (keyword: string): Promise<CMSSearchResult[] | null> => {
  const searchResponse = await searchCategoryNamesRaw(keyword);
  if (!searchResponse.success || !searchResponse.results) return null;

  const seenIds = new Set<string>();
  return Object.entries(searchResponse.results)
    .filter(([bucket, value]) => bucket !== 'home' && Array.isArray(value))
    .flatMap(([, value]) => value as CMSSearchResult[])
    .filter(cat => {
      if (cat.category_type !== 'custom' || seenIds.has(cat.id)) return false;
      seenIds.add(cat.id);
      return true;
    });
};

/**
 * Fetches a published custom CMS page by its URL slug.
 *
 * The public URL is based on the page's display_name, so the consumer and
 * trade versions of a page (e.g. "Outdoor (Consumer)" / "Outdoor (Trade)",
 * both display_name "Outdoor") share one slug (/outdoor). Resolution here
 * prefers the page whose display_name matches the slug AND is visible on
 * consumer (consumer_display), which also enforces the audience gate — a
 * trade-only page can't be opened on the consumer site by direct URL.
 *
 * Flow: slug → name → searchCategoryNamesRaw → pick matching custom category →
 * published 'consumer' role override if there is one, else getCMSPage
 */
export const getCustomCmsPageData = async (rawSlug: string): Promise<CMSApiResponse> => {
  // Normalise so links shared with a doubled dash (/stools-ottomans--benches)
  // still resolve to the same page as /stools-ottomans-benches.
  const slug = slugify(rawSlug);
  try {
    const categoryName = slugToName(slug);

    const candidates = await searchCustomCandidates(categoryName);

    if (!candidates) {
      throw new Error(`Custom page '${slug}' not found`);
    }

    // consumer_display defaults to true on the backend; treat missing as visible.
    const isConsumerVisible = (cat: CMSSearchResult) => cat.consumer_display !== false;

    const matchCustomCategory = (pool: CMSSearchResult[]) =>
      // 1) display_name slug match, visible on consumer (the intended path)
      pool.find(cat => cat.display_name && slugify(cat.display_name) === slug && isConsumerVisible(cat)) ??
      // 2) category_name slug match, visible on consumer (pages without a display_name)
      pool.find(cat => slugify(cat.category_name) === slug && isConsumerVisible(cat)) ??
      // 3) legacy exact category_name match, visible on consumer
      pool.find(cat => cat.category_name.toLowerCase() === categoryName.toLowerCase() && isConsumerVisible(cat));

    let customCategory = matchCustomCategory(candidates);

    // The backend search is a plain substring match on the page / display name, so
    // a name with punctuation ("Stools, ottomans & benches") never contains the
    // slug's words ("Stools Ottomans Benches"). Retry on the first word alone and
    // let the slug comparison above pick the right page.
    const firstWord = slug.split('-')[0];
    if (!customCategory && firstWord && firstWord !== slug) {
      const fallbackCandidates = await searchCustomCandidates(firstWord);
      if (fallbackCandidates) customCategory = matchCustomCategory(fallbackCandidates);
    }

    if (!customCategory) {
      // Either no such page, or it exists but isn't for the consumer storefront.
      throw new Error(`Custom page '${slug}' not available on consumer`);
    }

    // Role override FIRST: the hub can give the 'consumer' role its own version of
    // a custom page (same helper the normal consumer category pages use). The
    // override's blocks still carry the per-block Consumer/Trade toggle, which the
    // block renderers apply afterwards — so the order is override, then toggle.
    // A failed override load falls back to the default page instead of a blank one.
    const consumerOverrideId = getConsumerOverrideId(customCategory);
    if (consumerOverrideId) {
      try {
        const overrideResponse = await getCategoryOverrideData(consumerOverrideId, true);
        if (overrideResponse.success) {
          return overrideResponse;
        }
        console.warn(`Consumer override for custom page '${slug}' failed to load, using default page`);
      } catch (overrideError) {
        console.warn(`Consumer override for custom page '${slug}' failed to load, using default page`, overrideError);
      }
    }

    // Always fetch published only on the consumer site
    const cmsResponse = await getCMSPage(customCategory.id, true);

    if (!cmsResponse.success) {
      throw new Error(`Failed to load custom CMS page '${slug}'`);
    }

    return cmsResponse;
  } catch (error) {
    console.error(`Error fetching custom CMS page '${slug}':`, error);
    throw error;
  }
};

// ===== CATEGORY LINK BUILDING =====

// Get category details for link building
export const getCategoryDetails = async (categoryId: string, viewMode: CMSViewMode = 'live'): Promise<CMSApiResponse> => {
  try {
    const onlyPublished = true;

    const response = await baseURL.get(
      `/cms-category/${categoryId}/cms-page/${onlyPublished}`,
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    return response.data;
  } catch (error) {
    console.error('Error fetching category details:', error);
    throw error;
  }
};

// Build the correct URL for a category based on its type and data
export const buildCategoryUrl = (categoryData: any): string => {
  const { category_type, item_group, application, collection, brand, category_name } = categoryData;

  switch (category_type) {
    case 'collection':
      if (item_group && collection) {
        return `/item_group/${item_group}/collection/${encodeURIComponent(collection)}`;
      }
      break;

    case 'application':
      if (item_group && application) {
        return `/item_group/${item_group}/application/${safeUrlEncode(application)}`;
      }
      break;

    case 'item_group':
      if (item_group) {
        return `/item_group/${item_group}`;
      }
      break;

    case 'brand':
      if (brand) {
        return `/brand/${encodeURIComponent(brand)}`;
      }
      break;

    default:
      return `/category/${safeUrlEncode(category_name)}`;
  }

  return `/category/${categoryData.id}`;
};